import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import Job from '../../dto/DtoJob'
import i18n from 'simple-react-i18n'
import cronstrue from 'cronstrue/i18n'
import { template, trim, split, filter, map, includes } from 'lodash'
import Select from '../../../components/forms/Select'
import { getCurrentLang } from '../../../utils/LangUtils'
import { hasValue } from '../../../utils/NumberUtil'
import AlertAction from '../../../alerting/actions/AlertAction'
import { arrayOf } from '../../../utils/StoreUtils'
import JobChainStepper from './JobChainStepper'
import JobAction from '../../actions/JobAction'
import JobChainGraph from './JobChainGraph'
import { numberList } from 'utils/ListUtil'
import SieauParameterDto from '../../../administration/dto/SieauParameterDto'
import {
    Box, Button, Card, CardContent, Grid, Stack, Switch, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { CardTitle } from '../../../components/card/NewCard'
import { CardTable } from '../../../components/datatable/NewTable'
import { darkBlue, errorColor, mediumGrey } from '../../../utils/constants/ColorTheme'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import RadioButtons from '../../../components/forms/RadioButtons'
import { DEFAULT_AQUASYS_SUPPORT_EMAIL } from 'components/sieau/SieauConstants'
import SimpleInput from 'components/forms/SimpleInput'
import Checkbox from '../../../components/forms/Checkbox'
import { formatSingleCron } from '../../../utils/JobUtils'

const RECURRENCE_TYPE_WEEKLY = 'weekly'
const RECURRENCE_TYPE_MONTHLY = 'monthly'
const MONTHLY_TYPE_NUMBER = 'number'
const MONTHLY_TYPE_RELATIVE = 'relative'

const RELATIVE_RANGES = ['1-7', '8-14', '15-21', '22-28', '25-31']

const SchedulePanel = ({
    job,
    jobs,
    onChangePlanification,
    onChangeEmails,
    onChangeExecutionAfterError,
    onChangeIaeauTimeout,
    isEditMode,
}) => {
    const dispatch = useDispatch()

    const parseCron = (cronStr) => {
        if (!cronStr) return null
        const parts = split(trim(cronStr), /\s+/)
        if (parts.length < 7) return null
        const minutes = parts[1] === '*' ? '0' : parts[1]
        const hours = parts[2]
        const dayOfMonth = parts[3]
        const dayOfWeek = parts[5]

        if (includes(RELATIVE_RANGES, dayOfMonth)) {
            return {
                recurrence: RECURRENCE_TYPE_MONTHLY,
                monthlyType: MONTHLY_TYPE_RELATIVE,
                occurrence: dayOfMonth,
                relativeDayOfWeek: dayOfWeek,
                hours,
                minutes,
            }
        }
        if (dayOfMonth !== '*' && dayOfMonth !== '?') {
            return {
                recurrence: RECURRENCE_TYPE_MONTHLY,
                monthlyType: MONTHLY_TYPE_NUMBER,
                monthDay: dayOfMonth,
                hours,
                minutes,
            }
        }
        return {
            recurrence: RECURRENCE_TYPE_WEEKLY,
            monthlyType: MONTHLY_TYPE_NUMBER,
            days: dayOfWeek,
            hours,
            minutes,
        }
    }

    const initialSchedules = useMemo(() => {
        if (!job?.cron) return []
        return filter(map(split(job.cron, ';'), s => trim(s)), Boolean)
    }, [job?.cron])

    const [schedules, setSchedules] = useState(initialSchedules)
    const [planified, setPlanified] = useState(initialSchedules.length > 0)

    const firstParsed = useMemo(() => {
        if (initialSchedules.length > 0) {
            return parseCron(initialSchedules[0])
        }
        return null
    }, [initialSchedules])

    const [recurrence, setRecurrence] = useState(firstParsed?.recurrence || RECURRENCE_TYPE_WEEKLY)
    const [monthlyType, setMonthlyType] = useState(firstParsed?.monthlyType || MONTHLY_TYPE_NUMBER)
    const [occurrence, setOccurrence] = useState(firstParsed?.occurrence || '1-7')
    const [relativeDayOfWeek, setRelativeDayOfWeek] = useState(firstParsed?.relativeDayOfWeek || '6')
    const [days, setDays] = useState(firstParsed?.days || '1')
    const [monthDay, setMonthDay] = useState(firstParsed?.monthDay || '1')
    const [hours, setHours] = useState(firstParsed?.hours || '0')
    const [minutes, setMinutes] = useState(firstParsed?.minutes || '0')
    // Tableau des programmations affiché (mode multi) : dès qu'il y en a plusieurs, ou après un clic sur "Ajouter".
    // Avec une seule programmation (tableau masqué), les champs la modifient directement.
    const [showTable, setShowTable] = useState(false)
    const isMultiMode = schedules.length > 1 || showTable
    const [chainStepperIsOpen, setChainStepperIsOpen] = useState(false)
    const [selectedJob, setSelectedJob] = useState({})
    const [chain, setChain] = useState([])

    useEffect(() => {
        if (job.id) {
            JobAction.getJobChain(job.id).then(c => {
                if (c.includes(job.id) && c[0] !== job.id) {
                    setPlanified(false)
                }
                setChain(c)
            })
        }
    }, [job.id])

    // Remplit les champs du formulaire à partir d'une cron
    const loadFieldsFromCron = cronStr => {
        const parsed = parseCron(cronStr)
        if (parsed) {
            setRecurrence(parsed.recurrence)
            if (parsed.monthlyType) setMonthlyType(parsed.monthlyType)
            if (parsed.occurrence) setOccurrence(parsed.occurrence)
            if (parsed.relativeDayOfWeek) setRelativeDayOfWeek(parsed.relativeDayOfWeek)
            if (parsed.days) setDays(parsed.days)
            if (parsed.monthDay) setMonthDay(parsed.monthDay)
            if (parsed.hours) setHours(parsed.hours)
            if (parsed.minutes) setMinutes(parsed.minutes)
        }
    }

    useEffect(() => {
        if (job?.cron) {
            const list = filter(map(split(job.cron, ';'), s => trim(s)), Boolean)
            // Écho de notre propre mise à jour (onChangePlanification) : rien à resynchroniser,
            // sinon les champs repartiraient sur la 1re programmation pendant l'édition d'une autre.
            if (list.join(';') === schedules.join(';')) {
                return
            }
            setSchedules(list)
            setPlanified(list.length > 0)
            setShowTable(false)
            if (list.length > 0) {
                loadFieldsFromCron(list[0])
            }
        } else {
            setSchedules([])
            setPlanified(false)
        }
    }, [job?.cron])

    const hoursOptions = useMemo(() => [{ value: '*', label: i18n.everyHours }, ...numberList(24)], [])
    const minutesOptions = useMemo(() => [
        { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
        { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
        { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
        { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
        { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
        ...numberList(60),
    ], [])

    const daysOptions = useMemo(() => [
        { value: '*', label: i18n.allDays },
        { value: '1', label: i18n.monday },
        { value: '2', label: i18n.tuesday },
        { value: '3', label: i18n.wednesday },
        { value: '4', label: i18n.thursday },
        { value: '5', label: i18n.friday },
        { value: '6', label: i18n.saturday },
        { value: '0', label: i18n.sunday },
    ], [])

    const relativeDayOfWeekOptions = useMemo(() => [
        { value: '1', label: i18n.monday },
        { value: '2', label: i18n.tuesday },
        { value: '3', label: i18n.wednesday },
        { value: '4', label: i18n.thursday },
        { value: '5', label: i18n.friday },
        { value: '6', label: i18n.saturday },
        { value: '0', label: i18n.sunday },
    ], [])

    const occurrenceOptions = useMemo(() => [
        { value: '1-7', label: i18n.firstOccurrence },
        { value: '8-14', label: i18n.secondOccurrence },
        { value: '15-21', label: i18n.thirdOccurrence },
        { value: '22-28', label: i18n.fourthOccurrence },
        { value: '25-31', label: i18n.lastOccurrence },
    ], [])

    const monthDayOptions = useMemo(() => numberList(32).slice(1), [])

    const recurrenceOptions = [
        { code: RECURRENCE_TYPE_WEEKLY, name: i18n.weekly },
        { code: RECURRENCE_TYPE_MONTHLY, name: i18n.monthly },
    ]

    const monthlyTypeOptions = [
        { code: MONTHLY_TYPE_NUMBER, name: i18n.dayOfMonthNumber },
        { code: MONTHLY_TYPE_RELATIVE, name: i18n.relativeDayOfMonth },
    ]

    const buildSingleCron = ({
        recurrenceValue = recurrence,
        monthlyTypeValue = monthlyType,
        occurrenceValue = occurrence,
        relativeDayOfWeekValue = relativeDayOfWeek,
        monthDayValue = monthDay,
        daysValue = days,
        hoursValue = hours,
        minutesValue = minutes,
    } = {}) => {
        const dayOfMonth = recurrenceValue === RECURRENCE_TYPE_WEEKLY
            ? '*'
            : (monthlyTypeValue === MONTHLY_TYPE_RELATIVE ? occurrenceValue : monthDayValue)

        const dayOfWeek = recurrenceValue === RECURRENCE_TYPE_WEEKLY
            ? daysValue
            : (monthlyTypeValue === MONTHLY_TYPE_RELATIVE ? relativeDayOfWeekValue : '*')

        return ['0', minutesValue, hoursValue, dayOfMonth, '*', dayOfWeek, '*'].join(' ')
    }

    const updateSchedules = (newSchedulesList) => {
        setSchedules(newSchedulesList)
        if (newSchedulesList.length === 0) {
            setPlanified(false)
            onChangePlanification(null)
        } else {
            setPlanified(true)
            onChangePlanification(newSchedulesList.join(';'))
        }
    }

    // 1er clic (une seule programmation) : on passe en mode multi, le tableau apparaît avec la programmation
    // existante (les champs la représentent déjà, l'ajouter ferait un doublon).
    // Clics suivants : ajoute une ligne avec les paramètres sélectionnés.
    const handleAddSchedule = () => {
        const newCron = buildSingleCron()
        if (!isMultiMode) {
            setShowTable(true)
            if (!schedules.includes(newCron)) {
                updateSchedules([...schedules, newCron])
            }
            return
        }
        if (schedules.includes(newCron)) {
            dispatch(ToastrAction.warning(i18n.alreadyExists))
            return
        }
        updateSchedules([...schedules, newCron])
    }

    // Lignes uniquement supprimables. Retour à une seule programmation -> tableau masqué, les champs la reprennent.
    const handleDeleteSchedule = (index) => {
        const updated = schedules.filter((_, i) => i !== index)
        updateSchedules(updated)
        if (updated.length <= 1) {
            setShowTable(false)
            if (updated.length === 1) {
                loadFieldsFromCron(updated[0])
            }
        }
    }

    // Une seule programmation (tableau masqué) : les champs la modifient directement, donc c'est bien
    // la nouvelle valeur qui est enregistrée. En mode multi, les champs servent uniquement à l'ajout.
    const applyToSingleSchedule = overrides => {
        if (isEditMode && !isMultiMode && schedules.length === 1) {
            updateSchedules([buildSingleCron(overrides)])
        }
    }

    const handleChangeMinutes = (newMinutes) => {
        const isStepMinutes = newMinutes.includes('/')
        const newHours = isStepMinutes ? '*' : hours
        const newDays = isStepMinutes ? '*' : days

        setMinutes(newMinutes)
        if (isStepMinutes) {
            setHours(newHours)
            setDays(newDays)
        }
        applyToSingleSchedule({ minutesValue: newMinutes, hoursValue: newHours, daysValue: newDays })
    }

    const handleChangeHours = (newHours) => {
        const isStepHours = newHours.includes('/')
        const newDays = isStepHours ? '*' : days

        setHours(newHours)
        if (isStepHours) {
            setDays(newDays)
        }
        applyToSingleSchedule({ hoursValue: newHours, daysValue: newDays })
    }

    const handleChangeDays = selected => {
        const newDays = (hasValue(selected) && selected != -1) ? selected.toString() : '*'
        setDays(newDays)
        applyToSingleSchedule({ daysValue: newDays })
    }

    const handleChangeMonthDay = selected => {
        const newMonthDay = (hasValue(selected) && selected != -1) ? selected.toString() : '1'
        setMonthDay(newMonthDay)
        applyToSingleSchedule({ monthDayValue: newMonthDay })
    }

    const handleChangeRecurrence = selected => {
        const newRecurrence = selected || RECURRENCE_TYPE_WEEKLY
        setRecurrence(newRecurrence)
        applyToSingleSchedule({ recurrenceValue: newRecurrence })
    }

    const handleChangeMonthlyType = selected => {
        const newMonthlyType = selected || MONTHLY_TYPE_NUMBER
        setMonthlyType(newMonthlyType)
        applyToSingleSchedule({ monthlyTypeValue: newMonthlyType })
    }

    const handleChangeOccurrence = selected => {
        const newOcc = selected || '1-7'
        setOccurrence(newOcc)
        applyToSingleSchedule({ occurrenceValue: newOcc })
    }

    const handleChangeRelativeDayOfWeek = selected => {
        const newDay = selected || '6'
        setRelativeDayOfWeek(newDay)
        applyToSingleSchedule({ relativeDayOfWeekValue: newDay })
    }

    const handleChangeEmails = (e) => {
        const emails = e.split(';')
        onChangeEmails(emails)
    }

    const emails = useMemo(() => job.parameters?.emails?.join(';'), [job.parameters]) || DEFAULT_AQUASYS_SUPPORT_EMAIL

    return (
        <Card>
            <CardContent>
                <Stack spacing={2} paddingX={2}>
                    <Grid>
                        {i18n.emailOnJobFailed}
                        <SimpleInput
                            type='text'
                            placeholder={i18n.emailSelection}
                            value={emails}
                            onChange={v => handleChangeEmails(v)}
                            disabled={!isEditMode}
                        />
                    </Grid>
                    {(job.jobType && job.jobType.includes('alert')) && (
                        <Grid container spacing={2}>
                            {i18n.testMailSending}
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={!isEditMode}
                                onClick={() => dispatch(AlertAction.testMail(emails.split(';'), 'testDataMissing'))}
                            >
                                {i18n.missingData}
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={!isEditMode}
                                onClick={() => dispatch(AlertAction.testMail(emails.split(';'), 'testThresholdOverflow'))}
                            >
                                {i18n.thresholdOverflow}
                            </Button>
                        </Grid>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {i18n.jobScheduling}
                        <Switch
                            id='planification'
                            checked={planified}
                            disabled={!isEditMode}
                            onClick={() => {
                                if (chain.includes(job.id) && chain[0] !== job.id) {
                                    dispatch(ToastrAction.warning(i18n.jobChainError))
                                } else {
                                    const nextPlanified = !planified
                                    setPlanified(nextPlanified)
                                    if (!nextPlanified) {
                                        updateSchedules([])
                                    } else {
                                        const newCron = buildSingleCron()
                                        updateSchedules([newCron])
                                        setShowTable(false)
                                    }
                                }
                            }}
                        />
                    </Box>

                    {planified && (
                        <Stack spacing={2}>
                            <Grid container spacing={2} alignItems='flex-end'>
                                <Grid size={3}>
                                    <Box>
                                        <Typography variant='caption' sx={{ color: darkBlue, fontWeight: 600 }}>
                                            {i18n.recurrence}
                                        </Typography>
                                        <RadioButtons
                                            elements={recurrenceOptions}
                                            selected={recurrence}
                                            disabled={!isEditMode}
                                            onChange={handleChangeRecurrence}
                                        />
                                    </Box>
                                </Grid>
                                <Grid size={2}>
                                    <Select
                                        disabled={!isEditMode}
                                        value={hours || 0}
                                        options={hoursOptions}
                                        label={i18n.jobHour}
                                        onChange={handleChangeHours}
                                        noNullValue
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <Select
                                        disabled={!isEditMode}
                                        value={minutes || 0}
                                        options={minutesOptions}
                                        label={i18n.jobMinute}
                                        onChange={handleChangeMinutes}
                                        noNullValue
                                    />
                                </Grid>

                                {recurrence === RECURRENCE_TYPE_WEEKLY && (
                                    <Grid size={3}>
                                        <Select
                                            disabled={!isEditMode}
                                            value={days}
                                            options={daysOptions}
                                            label={i18n.dayOfWeek}
                                            onChange={handleChangeDays}
                                            noNullValue
                                            noSort
                                        />
                                    </Grid>
                                )}

                                {recurrence === RECURRENCE_TYPE_MONTHLY && (
                                    <>
                                        <Grid size={3}>
                                            <Box>
                                                <Typography variant='caption' sx={{ color: darkBlue, fontWeight: 600 }}>
                                                    {i18n.monthlyType}
                                                </Typography>
                                                <RadioButtons
                                                    elements={monthlyTypeOptions}
                                                    selected={monthlyType}
                                                    disabled={!isEditMode}
                                                    onChange={handleChangeMonthlyType}
                                                />
                                            </Box>
                                        </Grid>
                                        {monthlyType === MONTHLY_TYPE_NUMBER ? (
                                            <Grid size={2}>
                                                <Select
                                                    disabled={!isEditMode}
                                                    value={monthDay}
                                                    options={monthDayOptions}
                                                    label={i18n.day}
                                                    onChange={handleChangeMonthDay}
                                                    noNullValue
                                                    noSort
                                                />
                                            </Grid>
                                        ) : (
                                            <>
                                                <Grid size={2}>
                                                    <Select
                                                        disabled={!isEditMode}
                                                        value={occurrence}
                                                        options={occurrenceOptions}
                                                        label={i18n.occurrence}
                                                        onChange={handleChangeOccurrence}
                                                        noNullValue
                                                        noSort
                                                    />
                                                </Grid>
                                                <Grid size={2}>
                                                    <Select
                                                        disabled={!isEditMode}
                                                        value={relativeDayOfWeek}
                                                        options={relativeDayOfWeekOptions}
                                                        label={i18n.dayOfWeek}
                                                        onChange={handleChangeRelativeDayOfWeek}
                                                        noNullValue
                                                        noSort
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}

                                {isEditMode && (
                                    <Grid container size={2} alignItems='center'>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            startIcon={<AddIcon />}
                                            onClick={handleAddSchedule}
                                            sx={{ textTransform: 'none', height: 40, width: '100%' }}
                                        >
                                            {i18n.addSchedule}
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>

                            {isMultiMode && (
                                <CardTable
                                    title={i18n.schedules}
                                    color={darkBlue}
                                    headers={['schedule']}
                                    rows={schedules.map((cronStr, index) => ({
                                        id: index,
                                        schedule: {
                                            value: formatSingleCron(cronStr),
                                            leftIcon: { icon: 'access_time', color: mediumGrey },
                                        },
                                    }))}
                                    lineActions={[
                                        {
                                            icon: 'delete',
                                            onClick: row => handleDeleteSchedule(row.id),
                                            displayed: isEditMode && schedules.length > 1,
                                            color: errorColor,
                                            tooltip: i18n.deleteSchedule,
                                        },
                                    ]}
                                    displayHeaders={false}
                                />
                            )}
                        </Stack>
                    )}

                    <Card>
                        <CardTitle color={darkBlue} title={i18n.treatmentChain} />
                        <CardContent>
                            <Grid container direction='row' alignItems='center' spacing={1}>
                                {i18n.continueProcessAfterErrorProcessing}
                                <Checkbox
                                    checked={job.executionAfterError}
                                    onChange={() => {
                                        onChangeExecutionAfterError(!job.executionAfterError)
                                    }}
                                    disabled={ !isEditMode }

                                />
                            </Grid>
                            {i18n.treatmentModifyChain}
                            {jobs?.length && (
                                <JobChainGraph
                                    jobs={jobs}
                                    chain={chain}
                                    onClick={(newSelectedJob) => {
                                        setChainStepperIsOpen(true)
                                        setSelectedJob(newSelectedJob)
                                    }}
                                    selectedJob={job}
                                />
                            )}
                        </CardContent>
                    </Card>
                    { chainStepperIsOpen &&
                        <JobChainStepper
                            isOpen={chainStepperIsOpen}
                            setIsOpen={setChainStepperIsOpen}
                            chain={chain}
                            changeChain={setChain}
                            jobs={jobs}
                            selectedJob={selectedJob}
                            currentJob={job}
                            isEditMode={isEditMode}
                        />
                    }
                </Stack>
            </CardContent>
        </Card>
    )
}

SchedulePanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    jobs: arrayOf(Job),
    onChangePlanification: PropTypes.func.isRequired,
    onChangeEmails: PropTypes.func.isRequired,
    onChangeContactsIds: PropTypes.func.isRequired,
    onChangeExecutionAfterError: PropTypes.func,
    onChangeIaeauTimeout: PropTypes.func,
    isEditMode: PropTypes.bool,
    testMail: PropTypes.func,
    applicationSettings: arrayOf(SieauParameterDto),
}

export default SchedulePanel
